import { Button, IconButton } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { Message, MessageType } from "components/Message/Message";
import { Text } from "components/Text/Text";
import { getConfig } from "config/config";
import { useOutsideClick } from "helpers/hooks/useOutsideClick";
import { DevEnvironmentSettingsStore } from "./DevEnvironmentSettingsStore";
import styles from "./DevEnvironmentSettings.module.scss";

// Helper to safely access import.meta.env (handles test environment)
const getEnvValue = (key: string): string | undefined => {
  try {
    return (import.meta.env as any)?.[key];
  } catch {
    return undefined;
  }
};

export const DevEnvironmentSettings: FC = observer(() => {
  const [isOpen, setIsOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const config = getConfig();
  const location = useLocation();
  const store = useMemo(() => new DevEnvironmentSettingsStore(), []);

  // Check if on /director or /identity routes
  const isOnDirectorRoute = location.pathname.includes("/director");
  const isOnIdentityRoute = location.pathname.includes("/identity");

  const handleOutsideClick = useCallback(
    (node: Node) => {
      // Check if click is inside drawer or button
      if (drawerRef.current?.contains(node) || buttonRef.current?.contains(node)) {
        return;
      }
      setIsOpen(false);
      store.clearMessages();
    },
    [store],
  );

  const handleCreateMobile = useCallback(async () => {
    await store.setE2EMobile();
  }, [store]);

  const handleMockSumsub = useCallback(async () => {
    await store.setSumsub();
  }, [store]);

  const handleMockDirectorInfo = useCallback(async () => {
    await store.fillDirectorInfo();
  }, [store]);

  // Fetch and store alias on mount
  useEffect(() => {
    void store.fetchAndStoreAlias();
  }, [store]);

  useOutsideClick({ callbackFn: handleOutsideClick, addEvents: isOpen, preventBlur: false });

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        store.clearMessages();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }

    return undefined;
  }, [isOpen, store]);

  const environmentInfo = [
    {
      label: "Region",
      value: config.region,
    },
    {
      label: "Environment",
      value: getEnvValue("VITE_ANNA_DEV_ENV") || "local",
    },
    {
      label: "Backend URL",
      value: config.annaApiUrl,
    },
    {
      label: "App Version",
      value: config.appVersion,
    },
    {
      label: "Build Mode",
      value: getEnvValue("MODE") || "test",
    },
    {
      label: "User Alias",
      value: store.isFetchingAlias ? "Loading..." : store.alias || "Not available",
    },
  ];

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <div ref={buttonRef} className={styles.floatingButton}>
          <IconButton
            icon="systemInfo"
            title="Open environment settings"
            aria-label="Open environment settings"
            aria-expanded={isOpen}
            onClick={() => {
              setIsOpen(true);
            }}
          />
        </div>
      )}

      {/* Overlay */}
      {isOpen && <div className={classNames(styles.overlay, { [styles.open]: isOpen })} />}

      {/* Drawer */}
      {isOpen && (
        <div
          ref={drawerRef}
          className={classNames(styles.drawer, { [styles.open]: isOpen })}
          role="dialog"
          aria-modal="true"
          aria-labelledby="env-settings-title"
        >
          <div className={styles.header}>
            <div id="env-settings-title" className={styles.title}>
              <Text weight="semibold" size="l">
                Environment Settings
              </Text>
            </div>
            <IconButton
              icon="actionCloseCircleBorder"
              title="Close"
              aria-label="Close environment settings"
              onClick={() => {
                setIsOpen(false);
                store.clearMessages();
              }}
            />
          </div>

          <div className={styles.content}>
            <div className={styles.infoCard}>
              {environmentInfo.map((info) => (
                <div key={info.label} className={styles.infoRow}>
                  <Text className={styles.label} size="s" weight="semibold">
                    {info.label}:
                  </Text>
                  <Text className={styles.value} size="s">
                    {info.value}
                  </Text>
                </div>
              ))}
            </div>

            {/* E2E Testing Actions - Show on /director or /identity routes */}
            {(isOnDirectorRoute || isOnIdentityRoute) && (
              <div className={styles.actionSection}>
                <Text weight="semibold" size="s" className={styles.sectionTitle}>
                  E2E Testing Actions
                </Text>

                {/* Success Message */}
                {store.lastSuccess && (
                  <Message type={MessageType.Info} icon="actionTick" className={styles.message}>
                    {store.lastSuccess}
                  </Message>
                )}

                {/* Error Message */}
                {store.lastError && (
                  <Message type={MessageType.Error} icon="notificationTriangle" className={styles.message}>
                    {store.lastError}
                  </Message>
                )}

                {/* Director Route Buttons */}
                {isOnDirectorRoute && (
                  <>
                    <Button
                      text={store.isSettingMobile ? "Setting Mobile..." : "Create Mobile"}
                      onClick={handleCreateMobile}
                      disabled={store.isSettingMobile || store.isFillingDirector}
                      className={styles.actionButton}
                      size="l"
                    />
                    <Button
                      text={store.isFillingDirector ? "Filling Director Info..." : "Mock Director Info"}
                      onClick={handleMockDirectorInfo}
                      disabled={store.isFillingDirector || store.isSettingMobile}
                      className={styles.actionButton}
                      size="l"
                      type="secondary"
                    />
                  </>
                )}

                {/* Mock Sumsub Button - Only on /identity route */}
                {isOnIdentityRoute && (
                  <Button
                    text={store.isSettingSumsub ? "Mocking Sumsub..." : "Mock Sumsub"}
                    onClick={handleMockSumsub}
                    disabled={store.isSettingSumsub}
                    className={styles.actionButton}
                    size="l"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
});
